import {Component, inject} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatSelectModule} from '@angular/material/select';
import {TranslateModule} from '@ngx-translate/core';
import {DocItem} from '@services/api/admin/documents-admin-api.service';

export interface MoveDialogData {
  item: DocItem;
  folders: DocItem[];
}

interface FolderOption {
  id: number;
  label: string;
}

/** Scelta della cartella di destinazione. Restituisce l'id (0 = cartella principale). */
@Component({
  selector: 'app-move-dialog',
  standalone: true,
  imports: [FormsModule, MatDialogModule, MatFormFieldModule, MatSelectModule, TranslateModule],
  template: `
    <h2 mat-dialog-title>{{ 'admin.docs_move_title' | translate: {name: data.item.name} }}</h2>
    <mat-dialog-content>
      <mat-form-field appearance="outline" class="dm-a-field">
        <mat-label>{{ 'admin.docs_destination' | translate }}</mat-label>
        <mat-select [(ngModel)]="target">
          <mat-option [value]="0">{{ 'admin.docs_root' | translate }}</mat-option>
          @for (option of options; track option.id) {
            <mat-option [value]="option.id">{{ option.label }}</mat-option>
          }
        </mat-select>
      </mat-form-field>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button type="button" class="dm-a-btn ghost" mat-dialog-close>{{ 'admin.cancel' | translate }}</button>
      <button type="button" class="dm-a-btn primary" [disabled]="target === (data.item.parent ?? 0)" (click)="confirm()">
        {{ 'admin.docs_move' | translate }}
      </button>
    </mat-dialog-actions>
  `,
})
export class MoveDialogComponent {
  data: MoveDialogData = inject(MAT_DIALOG_DATA);
  private dialogRef = inject(MatDialogRef<MoveDialogComponent, number>);
  target = this.data.item.parent ?? 0;
  readonly options: FolderOption[] = this.buildOptions();

  confirm(): void {
    this.dialogRef.close(this.target);
  }

  /** Percorso completo di ogni cartella; una cartella non si sposta in se stessa o nelle sue sottocartelle. */
  private buildOptions(): FolderOption[] {
    const byId = new Map(this.data.folders.map((f) => [f.id, f]));
    const pathOf = (folder: DocItem): DocItem[] => {
      const chain: DocItem[] = [];
      let current: DocItem | undefined = folder;
      while (current && chain.length < 50) {
        chain.unshift(current);
        current = current.parent ? byId.get(current.parent) : undefined;
      }
      return chain;
    };
    return this.data.folders
      .filter((f) => this.data.item.type !== 'folder' || !pathOf(f).some((p) => p.id === this.data.item.id))
      .map((f) => ({id: f.id, label: pathOf(f).map((p) => p.name).join(' / ')}))
      .sort((a, b) => a.label.localeCompare(b.label));
  }
}
